import { useState } from "react";
import { Link } from "react-router-dom";
import { useProviders, ProviderWithProducts } from "../../../hooks/useProviders";
import { useApp } from "../../../contexts/AppContext";

function ProviderCard({ provider, index }: { provider: ProviderWithProducts; index: number }) {
  const [hovered, setHovered] = useState(false);
  const { toggleFollow, followedProviders, isLoggedIn } = useApp();
  const isFollowing = followedProviders.has(provider.id);

  const avatar =
    provider.avatar_url ||
    `https://public.readdy.ai/ai/img_res/2116aa36a3338bf0f18614debafd1d12.jpg${provider.id.slice(0, 8)}&orientation=squarish`;

  const cover =
    provider.cover_image_url ||
    `https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20artisan%20workshop%20tools%20elegant%20moody%20atmosphere%20dark%20warm%20lighting%20professional&width=480&height=280&seq=feat-cv-${provider.id.slice(0, 8)}&orientation=landscape`;

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden cursor-pointer group"
      style={{
        animationDelay: `${index * 0.1}s`,
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hovered ? "0 20px 40px rgba(30,61,47,0.13)" : "0 2px 12px rgba(30,61,47,0.06)",
        transition: "transform 0.32s ease, box-shadow 0.32s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={cover}
          alt={provider.service_name ?? provider.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-deep-green/40 to-transparent" />
        <div className="absolute bottom-3 left-4">
          <img
            src={avatar}
            alt={provider.name}
            className="w-14 h-14 rounded-full object-cover border-2 border-white"
          />
        </div>
        <div className="absolute top-3 right-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${provider.plan === "light" ? "bg-gold text-white" : "bg-white/80 text-charcoal"}`}>
            {provider.plan === "light" ? "ライトプラン" : "フリープラン"}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-2">
          <h3 className="font-serif text-deep-green text-base font-medium leading-snug">
            {provider.service_name || provider.name}
          </h3>
          <p className="text-charcoal/60 text-xs font-sans mt-0.5">
            {provider.name} · {provider.location || "日本"}
          </p>
        </div>
        {provider.slogan && (
          <p className="font-display italic text-teal text-sm mb-3 leading-snug">"{provider.slogan}"</p>
        )}
        {provider.skills && provider.skills.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {provider.skills.slice(0, 3).map((s) => (
              <span key={s} className="tag-pill text-xs">{s}</span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-charcoal/60 font-sans mb-3">
          <span className="flex items-center gap-1">
            <span className="star-gold">★</span>
            <strong className="text-charcoal">{(provider.rating ?? 0).toFixed(1)}</strong>
            <span>({provider.review_count ?? 0})</span>
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-follow-line" />
            {(provider.follower_count ?? 0).toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line" />
            {provider.products.length}本
          </span>
        </div>

        {provider.products.length > 0 && (
          <div className="flex gap-1.5 mb-4">
            {provider.products.slice(0, 3).map((p) => (
              <div key={p.id} className="flex-1 h-16 rounded-lg overflow-hidden">
                <img
                  src={p.thumbnail_url || `https://readdy.ai/api/search-image?query=craft%20workshop%20artisan%20tools%20minimal%20clean%20product%20photography&width=160&height=90&seq=thumb-${p.id.slice(0,8)}&orientation=landscape`}
                  alt={p.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={(e) => { e.preventDefault(); if (isLoggedIn) toggleFollow(provider.id); }}
            className={`flex-1 py-2 rounded-full text-xs font-sans font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${isFollowing ? "bg-teal text-white" : "border border-teal text-teal hover:bg-teal hover:text-white"}`}
          >
            {isFollowing ? "✓ フォロー中" : "+ フォロー"}
          </button>
          <Link
            to={`/providers/${provider.id}`}
            className="flex-1 py-2 rounded-full text-xs font-sans font-medium whitespace-nowrap cursor-pointer bg-deep-green text-white text-center transition-all duration-300 hover:bg-teal"
          >
            詳細を見る →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedProviders() {
  const { providers, loading } = useProviders();
  const featured = providers.slice(0, 6);

  if (loading) {
    return (
      <section className="bg-ivory py-20 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-gold text-xs font-display italic tracking-widest mb-2">Featured Artisans</p>
              <h2 className="section-title text-3xl md:text-4xl font-light">注目の職人・専門家</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-stone-100 animate-pulse">
                <div className="h-44 bg-stone-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-stone-200 rounded w-3/4" />
                  <div className="h-3 bg-stone-200 rounded w-1/2" />
                  <div className="h-8 bg-stone-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-ivory py-20 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Featured Artisans</p>
            <h2 className="section-title text-3xl md:text-4xl font-light">注目の職人・専門家</h2>
          </div>
          <Link
            to="/providers"
            className="text-teal text-sm font-sans hover:text-deep-green transition-colors whitespace-nowrap cursor-pointer hidden sm:block"
          >
            すべての事業者を見る →
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
            <i className="ri-store-2-line text-5xl text-charcoal/20 block mb-4" />
            <p className="text-charcoal/50 font-sans text-sm mb-4">まだ事業者が登録されていません</p>
            <Link
              to="/register"
              className="bg-deep-green text-white px-6 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
            >
              事業者として最初に登録する →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <ProviderCard key={p.id} provider={p} index={i} />
            ))}
          </div>
        )}
        <div className="text-center mt-8 sm:hidden">
          <Link to="/providers" className="btn-outline inline-block">すべての事業者を見る →</Link>
        </div>
      </div>
    </section>
  );
}
